import * as React from "react";
import {
  useDrag,
  useDrop,
  useDragLayer
} from "react-dnd";
import type {
  XYCoord,
  Identifier
} from "dnd-core";
import styled, { css } from "styled-components";
import * as _ from "lodash";
import { useHistory } from "react-router-dom";

import { Icon } from "src/library/components/Icon";
import { ActionButton } from "src/library/components/Button";
import { ITerminology } from "src/library/utils";
import { Text } from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";
import { CategoryChildRow } from "src/core/products/components/CategoryChildRow";

import { IProductCategory } from "../api/products.api";

export interface CategoryDraggableCardProps extends Pick<IProductCategory, "name" | "id"> {
  sortOrder: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  terminology: ITerminology;
  onDeleteCategory: (category: Pick<IProductCategory, "name" | "id">) => void;
  childCategories: IProductCategory[];
  sortOrderChanged: boolean;
  updateSortOrderChanged: (sortOrderChanged: boolean) => void;
}

export const ItemTypes = { CARD: "card" };

interface DragItem {
  index: number;
  id: string;
  type: string;
}

/**
 * Dragable parent category view
 *
 * @returns a category row react element
 */
export const CategoryDraggableCard: React.ReactFCWithChildren<CategoryDraggableCardProps> = ({
  id,
  name,
  sortOrder,
  moveCard,
  terminology,
  onDeleteCategory,
  childCategories,
  sortOrderChanged,
  updateSortOrderChanged
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const previewRef = React.useRef<HTMLDivElement>(null);
  const history = useHistory();
  const [ showChildren, setShowChildren ] = React.useState(false);

  const isDraggingLayer = useDragLayer(monitor => {
    return monitor.isDragging();
  });

  const [ { handlerId }, drop ] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: ItemTypes.CARD,
    collect(monitor) {
      // let consumer component know the order has changed
      if (monitor.didDrop() && !sortOrderChanged) {
        updateSortOrderChanged(true);
      }

      return { handlerId: monitor.getHandlerId() };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = sortOrder;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    }
  });

  const [
    { isDragging },
    drag,
    dragPreview
  ] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      return {
        id,
        index: sortOrder
      };
    },
    collect: monitor => ({ isDragging: monitor.isDragging() })
  });

  const opacity = isDragging ? 0 : 1;

  drag(drop(ref));
  dragPreview(drop(previewRef));

  return (
    <CategoryDraggableCardWrapper>
      <div
        className="parent-category"
        ref={previewRef}
        data-handler-id={handlerId}
        style={{ opacity }}
      >
        <div
          ref={ref}
          className="drag-icon-wrapper"
        >
          <Icon
            icon={Icons.DRAG}
            size={15}
          />
        </div>

        <div className="draggable-category">
          <div className="category-details">
            <div className="icon-wrapper">
              {!!childCategories.length && (
                <Icon
                  icon={showChildren ? Icons.CHEVRONUP : Icons.CHEVRONDOWN}
                  onClick={() => setShowChildren(!showChildren)}
                />
              )}
            </div>

            <Text>
              {name}
            </Text>
          </div>

          <div className="category-button">
            <ActionButton
              title="Actions"
              items={[
                {
                  title: `View/Edit ${terminology.category.singular.text}`,
                  action: () => {
                    history.push(`/admin/products/categories/${id}`);
                  }
                },
                {
                  title: `Delete ${terminology.category.singular.text}`,
                  action: () => {
                    onDeleteCategory({
                      name,
                      id
                    });
                  }
                }
              ]}
            />
          </div>
        </div>

      </div>

      {showChildren && !isDraggingLayer && _.map(childCategories, childCategory => (
        <div key={`child-category-${childCategory.id}`}>
          <CategoryChildRow
            id={childCategory.id}
            name={childCategory.name}
            terminology={terminology}
            onDeleteCategory={onDeleteCategory}
          />
        </div>
      ))}

    </CategoryDraggableCardWrapper>
  );
};

const CategoryDraggableCardWrapper = styled.div`
  ${({ theme }) => css`
    .drag-icon-wrapper {          
      svg {
        margin-right: ${theme.spacing.margins.md};

        :hover {
          cursor: grab;
        }
      }
    }

    .parent-category {
      display: flex;
      width: 100%;
      align-items: center;

      .icon-wrapper {
        width: 30px;

        svg:hover {
          cursor: pointer;
        }
      }
    }

    .draggable-category {
      width: 100%;
      background: ${theme.colors.white};
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md}; 
      border-bottom: ${theme.colors.lightGrey} 1px solid;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .category-details {
        display: flex;
        align-items: center;
      }
    }
  `}
`;
